import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { SiteSettingsRecord } from '../lib/siteSettingsDraft';

interface UseSiteSettingsPublishedOptions<T> {
  defaultValue: T;
  normalize?: (value: unknown) => T;
  mode?: 'published' | 'draft';
}

function cloneValue<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function resolveSiteSettingsPublished<T>(
  row: SiteSettingsRecord<T> | null | undefined,
  defaultValue: T,
  normalize?: (value: unknown) => T
): T {
  const normalizeValue = (value: unknown) =>
    normalize ? normalize(value) : ((value as T | null | undefined) ?? cloneValue(defaultValue));

  if (row?.published_content != null) return normalizeValue(row.published_content);
  if (import.meta.env.DEV) {
    if (row?.draft_content != null) return normalizeValue(row.draft_content);
    if (row?.content != null) return normalizeValue(row.content);
  }
  return cloneValue(defaultValue);
}

export function useSiteSettingsPublished<T>(
  key: string,
  options: UseSiteSettingsPublishedOptions<T>
) {
  const { defaultValue, normalize, mode = 'published' } = options;
  const [settings, setSettings] = useState<T>(() => cloneValue(defaultValue));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const selectColumns = useMemo(
    () =>
      mode === 'draft'
        ? 'key, content, draft_content, published_content'
        : import.meta.env.DEV
          ? 'key, content, draft_content, published_content'
          : 'key, published_content',
    [mode]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data, error } = await supabase
          .from('site_settings')
          .select(selectColumns)
          .eq('key', key)
          .maybeSingle<SiteSettingsRecord<T>>();

        if (cancelled) return;
        if (error) {
          setError(error);
          setSettings(cloneValue(defaultValue));
          return;
        }

        if (mode === 'draft') {
          const resolveDraft = (row: SiteSettingsRecord<T> | null | undefined) => {
            const normalizeValue = (value: unknown) =>
              normalize ? normalize(value) : ((value as T | null | undefined) ?? cloneValue(defaultValue));
            if (row?.draft_content != null) return normalizeValue(row.draft_content);
            if (row?.published_content != null) return normalizeValue(row.published_content);
            if (import.meta.env.DEV && row?.content != null) return normalizeValue(row.content);
            return cloneValue(defaultValue);
          };
          setSettings(resolveDraft(data));
        } else {
          setSettings(resolveSiteSettingsPublished(data, defaultValue, normalize));
        }
      } catch (err) {
        if (cancelled) return;
        setError(err);
        setSettings(cloneValue(defaultValue));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [defaultValue, key, mode, normalize, selectColumns]);

  return { settings, loading, error };
}

export default useSiteSettingsPublished;
